import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import appointmentApiController from "../services/appointment.service";

const centsFromInput = (value) => {
  const text = String(value || "").trim();
  if (!/^\d+(?:\.\d{0,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  const minor = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(minor) ? minor : null;
};

const XeroRefundLineAllocation = ({ active, formik }) => {
  const { sale_id, client_id, total_deposit_refund, xero_line_allocations: selected = [] } = formik.values;
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let current = true;
    setQuote(null);
    setError("");
    if (!active || !sale_id || !client_id) return () => { current = false; };
    appointmentApiController.refundXeroLines({ sale_id, client_id })
      .then((response) => {
        if (current) setQuote({ ...response.data, _sale_id: sale_id, _client_id: client_id });
      })
      .catch(() => {
        if (current) setError("Unable to load Xero refund items. Please retry before refunding.");
      });
    return () => { current = false; };
  }, [active, sale_id, client_id]);

  useEffect(() => {
    if (!active || !quote || !quote.xero_connected || quote._sale_id !== sale_id
      || quote._client_id !== client_id || quote.lines?.length !== 1) return;
    const minor = centsFromInput(total_deposit_refund);
    const line = quote.lines[0];
    if (!minor || minor > line.refundable_amount_minor) return;
    formik.setFieldValue("xero_line_allocations", [
      { line_item_id: line.line_item_id, amount_minor: minor },
    ]);
  }, [active, sale_id, client_id, quote, total_deposit_refund]);

  if (!active || !sale_id || !client_id) return null;
  if (error) return <p role="alert" className="text-danger">{error}</p>;
  if (!quote || quote._sale_id !== sale_id || quote._client_id !== client_id) {
    return <p>Loading Xero refund items…</p>;
  }
  if (!quote.xero_connected) return null;

  const selections = Array.isArray(selected) ? selected : [];
  return (
    <section aria-label="Xero refund item allocation" className="mt-3">
      <h6>Allocate refund to Xero invoice items</h6>
      <p className="small text-muted">Enter amounts for the items being refunded. Their total must equal the refund amount above.</p>
      {(quote.lines || []).map((line) => {
        const current = selections.find((item) => item.line_item_id === line.line_item_id);
        const label = `${line.description} refund amount`;
        return (
          <div className="row align-items-center mb-2" key={line.line_item_id}>
            <label className="col-md-7" htmlFor={`xero-refund-${line.line_item_id}`}>
              {line.description} (available {(line.refundable_amount_minor / 100).toFixed(2)} {quote.currency})
            </label>
            <div className="col-md-5">
              <input
                id={`xero-refund-${line.line_item_id}`}
                aria-label={label}
                type="number"
                min="0"
                max={(line.refundable_amount_minor / 100).toFixed(2)}
                step="0.01"
                className="form-control"
                value={current ? (current.amount_minor / 100).toFixed(2) : ""}
                onChange={(event) => {
                  const minor = centsFromInput(event.target.value);
                  const others = selections.filter((item) => item.line_item_id !== line.line_item_id);
                  formik.setFieldValue(
                    "xero_line_allocations",
                    minor === null || minor === 0 ? others : [...others, { line_item_id: line.line_item_id, amount_minor: minor }],
                  );
                }}
              />
            </div>
          </div>
        );
      })}
    </section>
  );
};

XeroRefundLineAllocation.propTypes = {
  active: PropTypes.bool,
  formik: PropTypes.object,
};

export default XeroRefundLineAllocation;
